import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, Box, Typography, Chip, Paper, Grid, useTheme, alpha } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '../../../../store';
import { CSPChargeType, HandleBookCSPType } from '../../../../store/reducers/GetRates/Type';
import { sortCSPs } from '../../../GetRatesV2/CSPList/SingleCSP';
import ChargesBreakup from '../../../GetRatesV2/CSPList/ChargesBreakup';
import { BackToLoginModal } from '../../../GetRatesV2/CSPList/BackToLoginModal';
import SbButton from '../../../../components/Button';
import SbTooltip from '../../../../components/Tooltip';
import CreateShipmentContext from '../../../../context/CreateShipment';
import { createShipmentContextType } from '../../../../types/shipment';
import { setBookNowAuth } from '@Reducers/GetRates';
import useAccountLookup from '@Hook/useAccountLookup';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getWarehouseListForShipment } from '@Reducers/Warehouse';
import { getSpecificWarehouseListForShipment } from '@Reducers/Warehouse/actions';
import { userLogin } from '@Services/hasAdminAccess';
import { isExportShipment } from '../../../../utils/exportShipment';
import { getDutyCharge } from '../../../../utils/pricing';

type Warehouse = {
  _id: string;
  _systemId: string[];
};

function getWarehouseIdBySystemId(
  systemId: string | undefined,
  warehouseListForShipment?: { list?: Warehouse[] },
): string | undefined {
  if (!systemId) return undefined;
  return warehouseListForShipment?.list?.find(warehouse => warehouse._systemId.includes(systemId))?._id;
}

interface HurricaneData {
  currency?: string | null;
  accuracy?: string;
  duty?: {
    amount?: number;
    currency?: string;
  };
  tax?: {
    amount?: number;
    currency?: string;
  };
  products?: Array<{
    description: string;
    dutyRate: number;
    taxRate: number;
  }>;
}

interface ShipmentServicesProps {
  handleBookCSP: HandleBookCSPType;
  setShowRates?: (show: boolean) => void;
}

const ShipmentServices: React.FC<ShipmentServicesProps> = ({ handleBookCSP }) => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();

  const CSPChargesData = useSelector((state: RootStateType) => state?.getRates?.CSPChargesData);
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};
  const defaultOrgCountryCurrency = useSelector(
    (state: RootStateType) => state.orgSetup.organizationData?.currency?.name || '',
  );
  const { warehouseListForShipmentOptionsHashById } = useSelector((state: RootStateType) => state.Warehouse);
  const { warehouseListForShipent } = useSelector((state: RootStateType) => state.Warehouse);
  const countryHashById = useSelector((state: RootStateType) => state.global.countriesObj);
  const accountNumber = useSelector((state: RootStateType) => state?.loginUser?.profile?.accountNumber) || '';
  const { token } = useSelector((state: RootStateType) => state.loginUser);
  const isUserLogin = userLogin();
  const { CreateShipment } = useSelector((state: RootStateType) => state.accountLookup);

  const modalId = 'GetRatesV2Main';
  const { selectedUser } = useAccountLookup(modalId);
  const isAccountSuspended = selectedUser?.onBoardedStatus ? selectedUser?.onBoardedStatus === 'suspended' : false;

  const shipmentContext = React.useContext(CreateShipmentContext) as createShipmentContextType;
  const pickupType = shipmentContext?.pickupType;
  const setPickupType = shipmentContext?.setPickupType;
  const packageData = shipmentContext?.packageData;
  const setPackageData = shipmentContext?.setPackageData;
  const shipmentType = shipmentContext?.shipmentType;
  const selectedCSP = shipmentContext?.selectedCSP;
  const shipper = shipmentContext?.shipper;
  const consignee = shipmentContext?.consignee;

  const [openBackToLoginModal, setBackToLoginModal] = useState(false);
  const [currency, setCurrency] = useState<{ name?: string; symbol?: string; fullForm?: string }>({});

  useEffect(() => {
    const stored = localStorage.getItem('currency');
    if (stored) {
      try {
        setCurrency(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse stored currency:', e);
      }
    }
  }, []);

  useEffect(() => {
    if (name === 'Cargo Solutions Network') {
      dispatch(getWarehouseListForShipment());
      dispatch(
        getSpecificWarehouseListForShipment(
          isUserLogin
            ? accountNumber
            : CreateShipment?.selectedUser?.accountNumber || CreateShipment?.selectedUser?._accountId?.accountNumber,
        ),
      );
    }
  }, [dispatch]);

  const hurricaneData: HurricaneData | undefined = CSPChargesData?.hurricaneData;
  const dutyChargeAmount = CSPChargesData?.dutyChargeAmount;

  const { shipmentById } = useSelector((state: RootStateType) => state.adminShipment);
  const organizationCountry = useSelector((state: RootStateType) => state.orgSetup.organizationData);
  const orgCountryName = organizationData?._country?.name?.trim() || organizationCountry?._country?.name?.trim() || '';
  const isExport = isExportShipment({
    orgCountryName,
    shipper,
    consignee,
    shipmentById,
  });

  const currentDutyType =
    (typeof shipmentType?.dutyCharge === 'object' ? shipmentType?.dutyCharge?.type : shipmentType?.dutyCharge) || 'DDU';
  const isDDP = currentDutyType.toUpperCase() === 'DDP';

  const sortedCharges = useMemo(() => {
    return sortCSPs(CSPChargesData?.charges || []);
  }, [CSPChargesData?.charges]);

  const bookCSP = (CSPCharge: CSPChargeType) => {
    const warehouseId = getWarehouseIdBySystemId(CSPCharge?._systemId, warehouseListForShipent);
    if (
      warehouseId &&
      name === 'Cargo Solutions Network' &&
      setPackageData &&
      setPickupType &&
      packageData &&
      pickupType
    ) {
      const selectedWarehouse = warehouseListForShipmentOptionsHashById[warehouseId];
      const coAttention = selectedWarehouse.coAttention;
      const [telephoneCode, number] = coAttention.contact;
      const countryId = selectedWarehouse._addressId.country;
      const warehouseAddress = {
        ...selectedWarehouse._addressId,
        country: countryHashById?.[countryId]?.name,
        name: selectedWarehouse.name,
        _countryId: countryHashById?.[countryId]?._id,
        phone: { telephoneCode, number, name: coAttention.name },
        email: coAttention.email,
      };
      setPackageData({
        ...packageData,
        isDropOff: true,
        ...(CSPCharge?.currency && {
          currency: CSPCharge.currency || defaultOrgCountryCurrency,
        }),
      });
      setPickupType({ ...pickupType, selectedWearhouseAddress: warehouseAddress, warehouse: warehouseId });
    }
    if (token?._id) {
      handleBookCSP(CSPCharge);
      dispatch(setBookNowAuth(true));
    } else {
      setBackToLoginModal(true);
      dispatch(setBookNowAuth(false));
    }
  };

  if (!sortedCharges || sortedCharges.length === 0) {
    return <></>;
  }

  return (
    <>
      <BackToLoginModal open={openBackToLoginModal} onClose={() => setBackToLoginModal(false)} />

      <Card
        elevation={0}
        sx={{
          borderRadius: '14px',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
          bgcolor: theme.palette.background.paper,
        }}
      >
        {/* Card Header */}
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
            bgcolor: alpha(theme.palette.primary.main, 0.02),
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LocalShippingOutlinedIcon />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>
                  Available Carrier Services
                </Typography>
                <Chip
                  label={`${sortedCharges.length} Option${sortedCharges.length > 1 ? 's' : ''}`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary' }}>
                Compare transit times, chargeable weight, and rates across available carriers
              </Typography>
            </Box>
          </Box>

          {CSPChargesData?.chargeableWeight && (
            <Chip
              label={`Chargeable Weight: ${CSPChargesData.chargeableWeight}`}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                bgcolor: alpha(theme.palette.text.primary, 0.06),
                color: 'text.primary',
              }}
            />
          )}
        </Box>

        {/* Card Body - Service Comparison Cards */}
        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Grid container spacing={2}>
            {sortedCharges.map((charge, ind) => {
              const {
                transitTime,
                serviceName,
                serviceImage,
                totalPricing,
                fastest,
                cheapest,
                pricing,
                taxPricing,
                displayChargeableWeight,
              } = charge;

              const currencyLabel = charge?.currency || defaultOrgCountryCurrency || currency?.name || 'INR';

              const rawAccuracy =
                charge?.accuracy || (charge as any)?.accuracy || hurricaneData?.accuracy || CSPChargesData?.accuracy;
              const accuracy = rawAccuracy
                ? String(rawAccuracy).charAt(0).toUpperCase() + String(rawAccuracy).slice(1).toLowerCase()
                : '';

              const fullTotal = Number(totalPricing) || 0;
              const extractedDuty = getDutyCharge(pricing);
              const effectiveDutyAmount =
                extractedDuty > 0
                  ? extractedDuty
                  : dutyChargeAmount !== undefined && dutyChargeAmount !== null && !isNaN(Number(dutyChargeAmount))
                  ? Number(dutyChargeAmount)
                  : hurricaneData?.duty?.amount
                  ? Number(hurricaneData.duty.amount) + (Number(hurricaneData.tax?.amount) || 0)
                  : 0;

              const cardTotal = isExport && !isDDP ? Math.max(0, fullTotal - effectiveDutyAmount) : fullTotal;

              const isSelected =
                Boolean(selectedCSP) &&
                selectedCSP?._systemId === charge?._systemId &&
                selectedCSP?.serviceName === charge?.serviceName;

              return (
                <Grid item xs={12} key={ind}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 2, sm: 2.5 },
                      borderRadius: '12px',
                      border: isSelected
                        ? `2px solid ${theme.palette.primary.main}`
                        : `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                      bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.02) : theme.palette.background.paper,
                      boxShadow: isSelected
                        ? `0 4px 16px ${alpha(theme.palette.primary.main, 0.12)}`
                        : '0 1px 3px 0 rgba(0, 0, 0, 0.03)',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.1)}`,
                      },
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      {/* Col 1: Carrier Logo */}
                      <Grid item xs={12} sm={2} md={1.75} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: { xs: '100%', sm: 90 },
                            height: 60,
                            borderRadius: '8px',
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            bgcolor: theme.palette.common.white,
                            p: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <img
                            src={serviceImage}
                            alt={serviceName}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        </Box>
                      </Grid>

                      {/* Col 2: Service Title, Badges & Transit Time */}
                      <Grid item xs={12} sm={isExport ? 3.5 : 4} md={isExport ? 3.5 : 4.25}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}
                          >
                            {name === 'Cargo Solutions Network' ? serviceName : serviceName}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            icon={<AccessTimeOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                            label={`${transitTime} Days`}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.72rem',
                              fontWeight: 600,
                              bgcolor: alpha(theme.palette.text.primary, 0.05),
                              color: 'text.secondary',
                              '& .MuiChip-icon': { color: 'text.secondary' },
                            }}
                          />
                          {fastest && (
                            <Chip
                              icon={<BoltIcon sx={{ fontSize: '14px !important' }} />}
                              label="Fastest"
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.72rem',
                                fontWeight: 700,
                                bgcolor: '#e8f5e9',
                                color: '#2e7d32',
                                '& .MuiChip-icon': { color: '#2e7d32' },
                              }}
                            />
                          )}
                          {cheapest && (
                            <Chip
                              icon={<AccountBalanceWalletIcon sx={{ fontSize: '14px !important' }} />}
                              label="Cheapest"
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.72rem',
                                fontWeight: 700,
                                bgcolor: '#e1f5fe',
                                color: '#0288d1',
                                '& .MuiChip-icon': { color: '#0288d1' },
                              }}
                            />
                          )}
                        </Box>
                      </Grid>

                      {/* Col 3: Chargeable Weight + DDP/DDU Duty & Charges (to the right, Export only) */}
                      <Grid item xs={isExport ? 12 : 6} sm={isExport ? 3.5 : 2.5} md={isExport ? 3.75 : 2.5}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 2.5, sm: 3 } }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.25 }}>
                              Chargeable Wt:
                            </Typography>
                            <Typography
                              sx={{ fontSize: '14px', fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}
                            >
                              {`${displayChargeableWeight?.toFixed(2)} kg`}
                            </Typography>
                            {accuracy && (
                              <Chip
                                label={`Accuracy: ${accuracy}`}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.6875rem',
                                  fontWeight: 600,
                                  bgcolor: alpha(theme.palette.info.main, 0.1),
                                  color: 'info.main',
                                  mt: 0.5,
                                }}
                              />
                            )}
                          </Box>

                          {isExport && (
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary', display: 'block', mb: 0.25 }}
                              >
                                Duty & Charges:
                              </Typography>
                              <Typography
                                sx={{ fontSize: '14px', fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}
                              >
                                {currencyLabel} {effectiveDutyAmount.toFixed(2)}
                              </Typography>
                              <Chip
                                label={isDDP ? 'Added to your charges' : 'Consignee pays on delivery'}
                                size="small"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.6875rem',
                                  bgcolor: isDDP
                                    ? alpha(theme.palette.primary.main, 0.1)
                                    : alpha(theme.palette.success.main, 0.12),
                                  color: isDDP ? theme.palette.primary.main : theme.palette.success.dark,
                                  height: 20,
                                  mt: 0.5,
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      </Grid>

                      {/* Col 4: Price, Breakup & Action CTA */}
                      <Grid
                        item
                        xs={isExport ? 12 : 6}
                        sm={isExport ? 3 : 3.5}
                        md={isExport ? 3 : 3.5}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: { xs: 'flex-end', sm: 'flex-end' },
                          justifyContent: 'center',
                          gap: 1,
                        }}
                      >
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 800,
                              color: 'primary.main',
                              lineHeight: 1.1,
                            }}
                          >
                            {cardTotal ? `${currencyLabel} ${parseFloat(cardTotal.toString()).toFixed(2)}` : '-'}
                          </Typography>

                          <SbTooltip
                            title={
                              <ChargesBreakup
                                pricing={pricing}
                                totalPricing={totalPricing}
                                taxPricing={taxPricing}
                                currency={currencyLabel}
                                dutyChargeAmount={effectiveDutyAmount}
                                isDDP={isDDP}
                              />
                            }
                            placement="left"
                          >
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                cursor: 'pointer',
                                color: 'primary.main',
                                mt: 0.25,
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.72rem' }}>
                                View Breakup
                              </Typography>
                            </Box>
                          </SbTooltip>
                        </Box>

                        <SbButton
                          variant={isSelected ? 'contained' : 'outlined'}
                          size="small"
                          endIcon={isSelected ? undefined : <ArrowForwardIcon />}
                          disabled={isAccountSuspended}
                          onClick={() => bookCSP(charge)}
                          sx={{
                            px: 2.5,
                            py: 0.75,
                            borderRadius: '8px',
                            fontWeight: 700,
                            fontSize: '0.825rem',
                            whiteSpace: 'nowrap',
                            boxShadow: isSelected ? `0 3px 10px ${alpha(theme.palette.primary.main, 0.25)}` : 'none',
                          }}
                        >
                          {isSelected ? 'Selected' : 'Select Service'}
                        </SbButton>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>
    </>
  );
};

export default ShipmentServices;
